import {
  type ColumnDef,
  PaginationState,
  SortingState,
  createColumnHelper,
} from "@tanstack/react-table";
import { useCallback, useMemo, useState } from "react";
import { useNavigate } from "react-router";

import { Box } from "@design-system/Box/Box";
import { Clickable } from "@design-system/Clickable/Clickable";
import { Table } from "@design-system/Table/Table";
import { Tag } from "@design-system/Tag/Tag";

import { routes } from "@pages/routes";

import { usePaginationWithParams } from "@utils/hooks/usePaginationParams";

import {
  ClientEmployeeActivityFragment,
  useClientEmployeesByActivityQuery,
} from "./ClientEmployeesMostActiveTable.g";

const PAGE_SIZE = 5;

const columnHelper = createColumnHelper<ClientEmployeeActivityFragment>();

type Props = {
  clientId: string;
  initialPageIndex?: number;
};

export const ClientEmployeesMostActiveTable = ({
  clientId,
  initialPageIndex = 0,
}: Props) => {
  const [sorting, setSorting] = useState<SortingState>([]);
  const [pagination, setPagination] = useState<PaginationState>({
    pageIndex: initialPageIndex,
    pageSize: PAGE_SIZE,
  });
  const clientAppsQuery = useClientEmployeesByActivityQuery({
    clientId,
    first: pagination.pageSize,
    sortBy: "-resource_group_count",
  });
  const navigate = useNavigate();

  const handlePaginationChange = usePaginationWithParams(
    pagination,
    setPagination,
  );
  const isClientsLoading = clientAppsQuery.isLoading;
  const isClientsFetching = clientAppsQuery.isFetching;

  const totalCount =
    clientAppsQuery.data?.clientEmployees?.totalCount ?? undefined;

  const data = useMemo(() => {
    return (
      clientAppsQuery.data?.clientEmployees?.edges
        .map((edge) => edge?.node)
        .filter((clientEmployee) => !!clientEmployee) ?? []
    );
  }, [clientAppsQuery.data]);

  const handleUserDetailsClick = useCallback(
    (clientEmployeeId: string) => {
      navigate(
        routes.portal.clients.client.clientEmployees.clientEmployee.dashboard.$buildPath(
          {
            params: { clientId, clientEmployeeId },
          },
        ),
        {
          viewTransition: true,
        },
      );
    },
    [clientId, navigate],
  );

  const columns = useMemo(
    () => [
      columnHelper.accessor("name", {
        id: "name",
        cell: (props) => {
          return (
            <Clickable
              onClick={() => handleUserDetailsClick(props.row.original.id)}
              outline="sm"
              stretch
            >
              <Table.UserCell name={props.getValue()} />
            </Clickable>
          );
        },
        footer: undefined,
      }),
      columnHelper.accessor("resourceGroupCount", {
        id: "resource_group_count",
        cell: (props) => {
          return (
            <Box justify="flex-end" stretch>
              <Tag
                text={(props.getValue() ?? 0).toString()}
                title={`${props.getValue() ?? 0} application groups used`}
              />
            </Box>
          );
        },
        footer: undefined,
      }),
    ] as ColumnDef<ClientEmployeeActivityFragment>[],
    [handleUserDetailsClick],
  );

  const handlePageSizeChange = useCallback((pageSize: number) => {
    setPagination({ pageIndex: 0, pageSize });
  }, []);

  return (
    <>
      <Table
        columns={columns}
        data={data}
        rowCount={totalCount}
        onPaginationChange={handlePaginationChange}
        pagination={pagination}
        isLoading={isClientsLoading}
        isFetching={isClientsFetching}
        onPageSizeChange={handlePageSizeChange}
        sorting={sorting}
        onSortingChange={setSorting}
        showPageSizeDropdown={false}
        showHeader={false}
        showFooter={false}
      />
    </>
  );
};
